// Accordion labels switched to sentence case (acronyms kept).
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { AccessSummaryTypes } from '@ariksa/inventory-core';
import {
  Accordion,
  Box,
  Checkbox,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react';
import filter from 'lodash/filter';
import { useSelector } from 'react-redux';

import { StackedCell, WithResourceIcon } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { CustomIconTypes } from 'components/Icons/types';
import {
  AccessScopeButtons,
  AccountCell,
  useAccessScope,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/AccessScope';
import { MetaAccordionItem } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetaAccordionItem';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { renderHighPrivilegesCell } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { useRedirectToGraph } from 'containers/ActiveCloudResource/hooks/useRedirectToGraph';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useDataTypeClassifiers } from 'containers/Visibility/Data/Components/hooks/useDataTypeClassifiers';
// Disabled until the backend sensitive-records API is available.
// import { useSensitiveRecordsByResource } from 'containers/Visibility/Data/Components/hooks/useSensitiveRecordsByResource';
import { renderDataTypeLabels } from 'containers/Visibility/Data/Components/hooks/utils';

import { getDataLabelsForDataTypes } from './utils';

export const IAMUserData: FC = () => {
  const { canAccessSummary } = useSelector(selectActiveResource);
  const { canAccessData } = canAccessSummary;
  const {
    getCanAccessSummary,
    updateActiveResource,
  } = useActiveResourceActions();
  const { getCloudNativeName, getCloudAgnosticName } = useResourceType();
  const { redirectToGraph } = useRedirectToGraph();
  const { classifiers } = useDataTypeClassifiers();
  const { onOpenMetadata } = useActiveResourceContext();
  const { getTabIndex } = useMetadataDrawerTabs();
  const [onlySensitiveData, setOnlySensitiveData] = useState(false);

  const handleDataSourceRowClick = useCallback(
    row => {
      const destinationType = row?.destination_native_name;
      if (!destinationType) return;

      // Open the selected destination resource in the metadata drawer's Data tab.
      updateActiveResource({
        resourceType: destinationType,
        uuid: row?.destination_uuid ?? '',
        accountId: row?.destination_account ?? '',
        resourceId: row?.destination_resource_id ?? '',
      });
      onOpenMetadata(getTabIndex(destinationType, MetadataTabs.Data));
    },
    [getTabIndex, onOpenMetadata, updateActiveResource],
  );

  useEffect(() => {
    getCanAccessSummary(AccessSummaryTypes.CanAccessData);
  }, [getCanAccessSummary]);

  const visibleDataSources = useMemo(
    () =>
      onlySensitiveData
        ? filter(
            canAccessData.data,
            source =>
              Array.isArray(source?.data_type_tags) &&
              source.data_type_tags.length > 0,
          )
        : canAccessData.data,
    [canAccessData.data, onlySensitiveData],
  );
  const {
    scope,
    setScope,
    scopedRows,
    scopeItems,
    hasExternal,
  } = useAccessScope(visibleDataSources ?? []);

  const name = {
    header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
    accessor: 'destination_name',
    render: ({ row, value }) => (
      <WithResourceIcon resourceType={row?.destination_native_name}>
        <StackedCell
          upper={value}
          lower={
            getCloudNativeName(row?.destination_native_name) +
            ' / ' +
            getCloudAgnosticName(row?.destination_agnostic_name)
          }
        />
      </WithResourceIcon>
    ),
  };

  const mechanism = {
    header: 'Mechanism',
    align: 'left',
    accessor: 'permission_entity_name',
    render: ({ row, value }) => (
      <Stack>
        {/* Display every permission entity native resource using its readable name. */}
        <Box>
          {(row?.permission_entity_natives_names ?? [])
            .map(nativeName => getCloudNativeName(nativeName))
            .join(', ') || value}
        </Box>

        <HStack>
          <Box>{row?.permission_entity_agnostic_name?.[1]}</Box>
          {/* Only show the remaining count when positive (`length - 2 &&`
              rendered "-1" for single-entry lists). */}
          {row?.permission_entity_agnostic_name?.length > 2 && (
            <Box>{row?.permission_entity_agnostic_name?.length - 2}</Box>
          )}
        </HStack>
      </Stack>
    ),
  };

  // One row, as the Permissions tab shows it: the CRUD badges, or "None".
  const highPrivileges = {
    header: 'High Privileges',
    accessor: 'crud_level_counts_high',
    render: renderHighPrivilegesCell,
  };

  // One list for both accounts: the account is a column rather than a heading,
  // so sources from either can be compared in one place.
  // Data labels (PII, PCI, ...) of the source's data types, shown with the
  // same colored chips as the Visibility data tables.
  const dataLabels = {
    header: 'Data Labels',
    accessor: 'data_type_tags',
    align: 'left',
    render: ({ value }) => {
      const labels = getDataLabelsForDataTypes(value, classifiers);
      return labels.length ? renderDataTypeLabels({ row: { labels } }) : '-';
    },
  };

  // Sensitive-record totals per destination, keyed by its uuid.
  // The current labels-count API does not return the right numbers for these
  // sources, so the call is disabled and the column shows '-'. Re-enable (or
  // swap in the new endpoint) once the backend provides it.
  // const recordSources = useMemo(
  //   () =>
  //     (canAccessData.data ?? []).map(row => ({
  //       uuid: row?.destination_uuid,
  //       account_id: row?.destination_account,
  //     })),
  //   [canAccessData.data],
  // );
  // const sensitiveRecordsBySource = useSensitiveRecordsByResource(
  //   recordSources,
  //   true,
  // );
  const sensitiveRecordsBySource: Record<string, number> = {};
  const sensitiveRecords = {
    header: 'Sensitive Records',
    accessor: 'destination_uuid',
    render: ({ value }) => {
      const count = sensitiveRecordsBySource[value];
      return count == null ? '-' : formatNumber(count, 1);
    },
  };

  const columns = [
    name,
    dataLabels,
    // Sensitive Data tags replaced by the source's sensitive-record count.
    sensitiveRecords,
    mechanism,
    {
      // The data source's own account, which is what `external` compares here.
      header: 'Account',
      accessor: 'destination_account',
      align: 'left',
      render: ({ row, value }) => (
        <AccountCell accountId={value} external={row?.external} />
      ),
    },
    highPrivileges,
    {
      header: '# Permissions',
      accessor: 'total_permission_count',
    },
  ];

  return (
    <Stack spacing={3}>
      <Checkbox
        isChecked={onlySensitiveData}
        onChange={event => setOnlySensitiveData(event.target.checked)}
        colorScheme="purple"
      >
        <Text>Only sensitive data</Text>
      </Checkbox>
      <Accordion allowMultiple defaultIndex={[0]}>
        <MetaAccordionItem
          iconType={CustomIconTypes.DataScan}
          label="Data sources that this identity can manage"
          totalCount={scopedRows?.length}
          isLoading={canAccessData.isLoading}
          rightHeader={() =>
            hasExternal ? (
              <AccessScopeButtons
                items={scopeItems}
                scope={scope}
                onChange={setScope}
              />
            ) : null
          }
          panel={
            <Table
              columns={columns}
              data={scopedRows}
              isLoading={canAccessData.isLoading}
              onRowClick={handleDataSourceRowClick}
              noDataMessage="No data sources found"
            />
          }
          iconBGColor="primary"
          onShowGraph={() => redirectToGraph('access', MetadataTabs.Data)}
        />
      </Accordion>
    </Stack>
  );
};
